/**
 * 视频生成工作台
 *
 * 路由：/video
 * 布局：左 420px 表单 + 右画布；下方最近作品
 *
 * 关键行为：
 * - 切换模型：自动调整可选模式 / 时长 / 分辨率 / 比例 / 高级参数
 * - 切换模式：动态显示参考图 / 首尾帧上传区
 * - 提交：批量创建 N 条任务，由 useVideoJobPolling hook 持续追踪状态
 * - 主页 tab：图片 ↔ 视频跳转
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Video as VideoIcon, Grid2X2, Wand2, Loader2 } from 'lucide-react';
import toast from '../../components/Toast';
import { AuthModal } from '../../components/AuthModal';
import { Navbar, Footer } from '../image';
import { api as imageApi } from '../../services/api';
import { usePlatformSession } from '../../platform/session';
import { deriveThumbnailUrl } from '../../utils/imageDisplay';
import {
  videoApi,
  type CameraMovement,
  type MotionIntensity,
  type VideoJobDetail,
  type VideoMode,
  type VideoModelPublicInfo,
} from './services/videoApi';
import {
  getAvailableModes,
  getModelCapabilitiesForMode,
  getDurationValuesFromSpec,
  isDurationInSpec,
} from './hooks/useVideoModelCapabilities';
import { VideoModeSelector } from './components/VideoModeSelector';
import { VideoModelSelector } from './components/VideoModelSelector';
import { VideoReferenceUploader } from './components/VideoReferenceUploader';
import { VideoFirstLastFrameUploader } from './components/VideoFirstLastFrameUploader';
import { VideoPromptInput } from './components/VideoPromptInput';
import { VideoDurationSelector } from './components/VideoDurationSelector';
import { VideoResolutionSelector } from './components/VideoResolutionSelector';
import { VideoAspectRatioSelector } from './components/VideoAspectRatioSelector';
import { VideoCameraMovementSelector } from './components/VideoCameraMovementSelector';
import { VideoMotionIntensitySelector } from './components/VideoMotionIntensitySelector';
import { VideoAudioToggle } from './components/VideoAudioToggle';
import { VideoAudioReferenceUploader } from './components/VideoAudioReferenceUploader';
import { VideoVideoReferenceUploader } from './components/VideoVideoReferenceUploader';
import { VideoCountSelector } from './components/VideoCountSelector';
import { VideoCreditsPreview } from './components/VideoCreditsPreview';
import { VideoCanvas } from './components/VideoCanvas';
import { VideoLightbox } from './components/VideoLightbox';
import { VideoRecentWorks } from './components/VideoRecentWorks';
import { useVideoJobPolling } from './hooks/useVideoJobPolling';
import { withAuthToken } from '../../utils/storageUrl';

const VIDEO_PROMPT_DRAFT_KEY = 'video_prompt_draft';
/** 最近一次生成成功的模型（浏览器记忆，下次进入默认选中） */
const VIDEO_LAST_USED_MODEL_KEY = 'video_last_used_model';
const DEFAULT_MAX_JOBS_PER_SUBMIT = 4;

function VideoPage() {
  const navigate = useNavigate();
  const {
    credits,
    isLoggedIn,
    refreshUser,
    setUser,
    applyCreditsDelta,
  } = usePlatformSession();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [siteName, setSiteName] = useState('AI 视频生成');

  // 模型与配置
  const [models, setModels] = useState<VideoModelPublicInfo[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [maxJobsPerSubmit, setMaxJobsPerSubmit] = useState(DEFAULT_MAX_JOBS_PER_SUBMIT);

  // 表单参数
  const [mode, setMode] = useState<VideoMode>('text2video');
  const [prompt, setPrompt] = useState('');
  const [referenceImages, setReferenceImages] = useState<string[]>([]);
  const [firstFrame, setFirstFrame] = useState('');
  const [lastFrame, setLastFrame] = useState('');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('720p');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [cameraMovement, setCameraMovement] = useState<CameraMovement | undefined>(undefined);
  const [motionIntensity, setMotionIntensity] = useState<MotionIntensity | undefined>(undefined);
  const [enableAudio, setEnableAudio] = useState(true);
  const [audioReferenceUrls, setAudioReferenceUrls] = useState<string[]>([]);
  const [videoReferenceUrls, setVideoReferenceUrls] = useState<string[]>([]);
  /** 参考视频时长列表（秒），与 videoReferenceUrls 下标对齐；服务端探测值优先 */
  const [videoRefDurations, setVideoRefDurations] = useState<number[]>([]);
  const [jobCount, setJobCount] = useState(1);

  const [submitting, setSubmitting] = useState(false);
  const [recentRefreshKey, setRecentRefreshKey] = useState(0);

  const polling = useVideoJobPolling();

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxJob, setLightboxJob] = useState<{
    url: string;
    cover: string | null;
  } | null>(null);

  /**
   * 「最近作品」选中预览：用户点击最近作品缩略图后，
   * 把视频显示在画布顶部（不自动播放，提供"填充提示词 / 填充参考图 / 下载"快捷按钮）
   */
  const [selectedRecentJob, setSelectedRecentJob] = useState<VideoJobDetail | null>(null);

  const currentModel = useMemo(
    () => models.find((m) => m.modelId === selectedModelId),
    [models, selectedModelId]
  );
  const previousModelIdRef = useRef<string>('');

  // 加载提示词草稿
  useEffect(() => {
    const draft = localStorage.getItem(VIDEO_PROMPT_DRAFT_KEY);
    if (draft) setPrompt(draft);
  }, []);
  // 保存草稿（防抖）
  useEffect(() => {
    const t = setTimeout(() => {
      if (prompt.trim()) localStorage.setItem(VIDEO_PROMPT_DRAFT_KEY, prompt);
      else localStorage.removeItem(VIDEO_PROMPT_DRAFT_KEY);
    }, 500);
    return () => clearTimeout(t);
  }, [prompt]);

  // 加载模型与系统配置
  useEffect(() => {
    videoApi
      .getPublicModels()
      .then((data) => {
        setModels(data);
        if (data.length > 0 && !selectedModelId) {
          // 优先恢复最近一次生成成功的模型；模型已下架则回退列表第一个
          const remembered = localStorage.getItem(VIDEO_LAST_USED_MODEL_KEY);
          setSelectedModelId(
            remembered && data.some((m) => m.modelId === remembered)
              ? remembered
              : data[0].modelId
          );
        }
      })
      .catch((err) => {
        console.error('加载视频模型失败:', err);
      });

    if (imageApi.isLoggedIn()) {
      videoApi
        .getPublicSystemConfig()
        .then((conf) => setMaxJobsPerSubmit(conf.maxJobsPerSubmit || DEFAULT_MAX_JOBS_PER_SUBMIT))
        .catch(() => {
          // 拿不到配置时沿用默认
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载站点名
  useEffect(() => {
    imageApi
      .getSiteName()
      .then((res) => {
        setSiteName(res.siteName);
        document.title = `${res.siteName} - 视频生成`;
      })
      .catch(() => {});
  }, []);

  // 切换模型时按模型默认值重置参数
  // 模型 / 模式变化时统一收紧所有相关参数到当前能力声明范围内
  // 关键点：
  //   - 模型切换会改变所有 mode 的能力，所以这里 dep 同时包含 modelId 和 mode
  //   - 任何超出新能力范围的字段都立即 clamp，避免提交时被后端拒
  //   - 数组类（reference / audio / video）超过上限的多余项被截掉
  useEffect(() => {
    if (!currentModel) return;

    const previousModelId = previousModelIdRef.current;
    const modelChanged = previousModelId !== currentModel.modelId;
    previousModelIdRef.current = currentModel.modelId;

    // 1. 模式：当前模式不可用时，回退到第一个可用
    const availableModes = getAvailableModes(currentModel);
    if (!availableModes.includes(mode)) {
      const fallback = availableModes[0];
      if (fallback) setMode(fallback);
      // 这一轮直接返回，等下一轮 mode 变更后再继续清理后续字段
      return;
    }

    // 2. 时长 / 分辨率 / 比例：根据当前模式对应渠道的能力重置
    const caps = getModelCapabilitiesForMode(currentModel, mode);
    if (!caps.available) return;

    const durationValues = getDurationValuesFromSpec(caps.supportedDurations);
    const defaultDuration = isDurationInSpec(caps.supportedDurations, currentModel.defaults.duration)
      ? currentModel.defaults.duration
      : durationValues[0] || 5;
    if (modelChanged) {
      setDuration(defaultDuration);
    } else if (!isDurationInSpec(caps.supportedDurations, duration)) {
      setDuration(defaultDuration);
    }

    const defaultResolution =
      caps.supportedResolutions.length > 0
        ? (caps.supportedResolutions.includes(currentModel.defaults.resolution)
            ? currentModel.defaults.resolution
            : caps.supportedResolutions[0])
        : currentModel.defaults.resolution;
    if (caps.supportedResolutions.length > 0) {
      if (modelChanged) {
        setResolution(defaultResolution);
      } else if (!caps.supportedResolutions.includes(resolution)) {
        setResolution(defaultResolution);
      }
    }

    const defaultAspectRatio =
      caps.supportedAspectRatios.length > 0
        ? (caps.supportedAspectRatios.includes(currentModel.defaults.aspectRatio)
            ? currentModel.defaults.aspectRatio
            : caps.supportedAspectRatios[0])
        : currentModel.defaults.aspectRatio;
    if (caps.supportedAspectRatios.length > 0) {
      if (modelChanged) {
        setAspectRatio(defaultAspectRatio);
      } else if (!caps.supportedAspectRatios.includes(aspectRatio)) {
        setAspectRatio(defaultAspectRatio);
      }
    }

    // 3. 高级能力关闭时清空相关字段
    if (!caps.enableAudio && enableAudio) setEnableAudio(false);
    if (!caps.enableCameraMovement && cameraMovement) setCameraMovement(undefined);
    if (!caps.enableMotionIntensity && motionIntensity) setMotionIntensity(undefined);

    // 4. 参考图 / 参考音频 / 参考视频：能力关闭则清空，否则按上限截断
    if (!caps.enableAudioReference) {
      if (audioReferenceUrls.length > 0) setAudioReferenceUrls([]);
    } else if (audioReferenceUrls.length > caps.maxAudioReferenceCount) {
      setAudioReferenceUrls(audioReferenceUrls.slice(0, caps.maxAudioReferenceCount));
    }
    if (!caps.enableVideoReference) {
      if (videoReferenceUrls.length > 0) {
        setVideoReferenceUrls([]);
        setVideoRefDurations([]);
      }
    } else if (videoReferenceUrls.length > caps.maxVideoReferenceCount) {
      setVideoReferenceUrls(videoReferenceUrls.slice(0, caps.maxVideoReferenceCount));
      setVideoRefDurations(videoRefDurations.slice(0, caps.maxVideoReferenceCount));
    }
    // 参考图：仅 img2video 模式才允许，其它模式由下面的"切模式清理"处理；
    // 模式内合法时按上限截断
    if (mode === 'img2video' && referenceImages.length > caps.maxReferenceImageCount) {
      setReferenceImages(referenceImages.slice(0, caps.maxReferenceImageCount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModel?.modelId, mode]);

  // 切换模式时清理无关素材
  useEffect(() => {
    if (mode === 'text2video') {
      setReferenceImages([]);
      setFirstFrame('');
      setLastFrame('');
    } else if (mode === 'img2video') {
      setFirstFrame('');
      setLastFrame('');
    } else if (mode === 'first_last_frame') {
      setReferenceImages([]);
    }
  }, [mode]);

  // 用户登录态变化时刷新积分
  useEffect(() => {
    if (!isLoggedIn) return;
    void refreshUser();
  }, [isLoggedIn, refreshUser]);

  const enabledModes = useMemo(() => {
    if (!currentModel) return { text2video: true, img2video: true, first_last_frame: true };
    const available = getAvailableModes(currentModel);
    return {
      text2video: available.includes('text2video'),
      img2video: available.includes('img2video'),
      first_last_frame: available.includes('first_last_frame'),
    };
  }, [currentModel]);

  // 当前模式下的能力（从对应渠道的 providerMetadata 读取）
  const currentCaps = useMemo(
    () => getModelCapabilitiesForMode(currentModel, mode),
    [currentModel, mode]
  );

  const resolutionCreditsMap = useMemo(() => {
    if (!currentModel) return {};

    const modeMultiplier = currentModel.modeMultipliers?.[mode] ?? 1;
    const map: Record<string, number> = {};

    for (const res of currentCaps.supportedResolutions) {
      const resolutionMultiplier = currentModel.resolutionMultipliers?.[res] ?? 1;
      map[res] = Math.ceil(
        currentModel.billingMode === 'per_call'
          ? currentModel.creditsPerCall * resolutionMultiplier * modeMultiplier
          : currentModel.creditsPerSecond * resolutionMultiplier * modeMultiplier
      );
    }

    return map;
  }, [currentModel, currentCaps.supportedResolutions, mode]);

  // prompt 引用 chip 的图片 src 解析：data:URL 直接显示，本地 API URL 加 token
  const resolvePromptRefSrc = (raw: string): string => {
    if (!raw) return raw;
    const previewSrc = deriveThumbnailUrl(raw, 'small') || raw;
    if (previewSrc.startsWith('data:')) return previewSrc;
    if (previewSrc.includes('/api/')) return withAuthToken(previewSrc);
    return previewSrc;
  };

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!prompt.trim()) {
      toast.error('请输入提示词');
      return;
    }
    if (mode === 'img2video' && referenceImages.length === 0) {
      toast.error('请上传至少一张参考图');
      return;
    }
    if (
      mode === 'img2video' &&
      currentCaps.allowedReferenceImageCounts &&
      currentCaps.allowedReferenceImageCounts.length > 0 &&
      !currentCaps.allowedReferenceImageCounts.includes(referenceImages.length)
    ) {
      toast.error(
        buildReferenceImageCountMessage(currentModel?.name || '当前模型', currentCaps.allowedReferenceImageCounts, referenceImages.length)
      );
      return;
    }
    if (mode === 'first_last_frame' && (!firstFrame || !lastFrame)) {
      toast.error('请同时上传首帧和尾帧');
      return;
    }

    setSubmitting(true);
    try {
      const result = await videoApi.generate({
        modelId: selectedModelId,
        mode,
        prompt: prompt.trim(),
        duration,
        resolution,
        aspectRatio,
        cameraMovement,
        motionIntensity,
        enableAudio,
        audioReferenceUrls: audioReferenceUrls.length > 0 ? audioReferenceUrls : undefined,
        videoReferenceUrls: videoReferenceUrls.length > 0 ? videoReferenceUrls : undefined,
        videoReferenceDurations:
          videoReferenceUrls.length > 0 ? videoRefDurations : undefined,
        referenceImages: mode === 'img2video' ? referenceImages : undefined,
        firstFrameImage: mode === 'first_last_frame' ? firstFrame : undefined,
        lastFrameImage: mode === 'first_last_frame' ? lastFrame : undefined,
        n: jobCount,
      });

      // 占位卡片立即上画布
      await polling.addJobIds(result.jobIds);

      // 扣完积分本地刷一下（函数式增减，避免闭包读到过期余额）
      applyCreditsDelta(-result.creditsUsed);

      // 清提示词
      setPrompt('');
      localStorage.removeItem(VIDEO_PROMPT_DRAFT_KEY);

      // 记住本次生成使用的模型，下次进入默认选中
      localStorage.setItem(VIDEO_LAST_USED_MODEL_KEY, selectedModelId);

      // 刷新最近作品
      setRecentRefreshKey((k) => k + 1);

      toast.success(`已提交 ${result.jobIds.length} 个任务，扣除 ${result.creditsUsed} 积分`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '提交失败';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (job: VideoJobDetail) => {
    const downloadUrl = job.downloadUrl || job.playUrl || job.resultUrl;
    if (!downloadUrl) return;
    try {
      const resp = await fetch(withAuthToken(downloadUrl));
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video-${job.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('下载失败:', err);
      window.open(withAuthToken(downloadUrl), '_blank');
    }
  };

  const submitDisabled =
    submitting ||
    !selectedModelId ||
    !prompt.trim() ||
    (mode === 'img2video' && referenceImages.length === 0) ||
    (mode === 'img2video' &&
      !!currentCaps.allowedReferenceImageCounts?.length &&
      !currentCaps.allowedReferenceImageCounts.includes(referenceImages.length)) ||
    (mode === 'first_last_frame' && (!firstFrame || !lastFrame));

  return (
    <div className="min-h-screen bg-light-bg" style={{ paddingTop: '64px' }}>
      <Navbar
        onRedeemCredits={() => (isLoggedIn ? navigate('/user') : setShowAuthModal(true))}
        onLogin={() => setShowAuthModal(true)}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(u) => setUser(u)}
        defaultMode="login"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-[420px,1fr] gap-6">
          <div className="space-y-4">
            {/* 顶部图片/视频/导演 tab 切换 */}
            <div className="card p-1.5 flex gap-1">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium tab-inactive"
              >
                <ImageIcon className="w-4 h-4" />
                图片
              </button>
              <button
                type="button"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium tab-active"
              >
                <VideoIcon className="w-4 h-4" />
                视频
              </button>
              <button
                type="button"
                onClick={() => navigate('/apps')}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium tab-inactive"
              >
                <Grid2X2 className="w-4 h-4" />
                应用中心
              </button>
            </div>

            <div className="card p-5 space-y-5">
              <VideoModeSelector value={mode} onChange={setMode} enabledModes={enabledModes} />
              <VideoModelSelector
                value={selectedModelId}
                onChange={setSelectedModelId}
                models={models}
              />

              {mode === 'img2video' && (
                <div className="space-y-1.5">
                  <VideoReferenceUploader
                    images={referenceImages}
                    onImagesChange={setReferenceImages}
                    maxCount={currentCaps.maxReferenceImageCount || 1}
                  />
                  {currentCaps.allowedReferenceImageCounts &&
                    currentCaps.allowedReferenceImageCounts.length > 0 && (
                      <p className="text-xs text-light-muted">
                        当前模型仅支持 {formatAllowedReferenceCounts(currentCaps.allowedReferenceImageCounts)}。
                      </p>
                    )}
                </div>
              )}
              {mode === 'first_last_frame' && (
                <VideoFirstLastFrameUploader
                  firstFrame={firstFrame}
                  lastFrame={lastFrame}
                  onFirstFrameChange={setFirstFrame}
                  onLastFrameChange={setLastFrame}
                />
              )}

              <VideoPromptInput
                value={prompt}
                onChange={setPrompt}
                mode={mode}
                referenceImages={mode === 'img2video' ? referenceImages : undefined}
                resolveImageSrc={resolvePromptRefSrc}
              />

              <VideoDurationSelector
                value={duration}
                onChange={setDuration}
                spec={currentCaps.supportedDurations}
              />
              <VideoResolutionSelector
                value={resolution}
                onChange={setResolution}
                options={currentCaps.supportedResolutions}
                creditsMap={resolutionCreditsMap}
                creditsUnitLabel={currentModel?.billingMode === 'per_call' ? '次' : '秒'}
              />
              <VideoAspectRatioSelector
                value={aspectRatio}
                onChange={setAspectRatio}
                options={currentCaps.supportedAspectRatios}
              />

              {currentCaps.enableCameraMovement && (
                <VideoCameraMovementSelector value={cameraMovement} onChange={setCameraMovement} />
              )}
              {currentCaps.enableMotionIntensity && (
                <VideoMotionIntensitySelector value={motionIntensity} onChange={setMotionIntensity} />
              )}
              {currentCaps.enableAudio && (
                <VideoAudioToggle value={enableAudio} onChange={setEnableAudio} />
              )}
              {currentCaps.enableAudioReference && (
                <VideoAudioReferenceUploader
                  value={audioReferenceUrls}
                  onChange={setAudioReferenceUrls}
                  maxCount={currentCaps.maxAudioReferenceCount}
                  minDurationSec={currentCaps.audioReferenceMinDurationSec}
                  maxDurationSec={currentCaps.audioReferenceMaxDurationSec}
                />
              )}
              {currentCaps.enableVideoReference && (
                <VideoVideoReferenceUploader
                  value={videoReferenceUrls}
                  durations={videoRefDurations}
                  onChange={(nextUrls, nextDurations) => {
                    setVideoReferenceUrls(nextUrls);
                    setVideoRefDurations(nextDurations);
                  }}
                  maxCount={currentCaps.maxVideoReferenceCount}
                  minDurationSec={currentCaps.videoReferenceMinDurationSec}
                  maxDurationSec={currentCaps.videoReferenceMaxDurationSec}
                  totalMaxDurationSec={currentCaps.videoReferenceTotalMaxDurationSec}
                />
              )}

              <VideoCountSelector value={jobCount} onChange={setJobCount} max={maxJobsPerSubmit} />

              <VideoCreditsPreview
                modelId={selectedModelId}
                mode={mode}
                duration={duration}
                resolution={resolution}
                n={jobCount}
                enableAudio={currentCaps.enableAudio ? enableAudio : false}
                refVideoDurationSec={
                  videoReferenceUrls.length > 0
                    ? videoRefDurations.reduce((sum, d) => sum + d, 0)
                    : 0
                }
                refImageCount={
                  (mode === 'img2video' ? referenceImages.length : 0) +
                  (mode === 'first_last_frame'
                    ? (firstFrame ? 1 : 0) + (lastFrame ? 1 : 0)
                    : 0)
                }
                userCredits={isLoggedIn ? credits : undefined}
              />

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base shadow-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    提交中...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    {isLoggedIn ? `生成${jobCount > 1 ? `${jobCount}个` : ''}视频` : '登录后生成'}
                  </>
                )}
              </button>
            </div>
          </div>

          <div>
            <div className="card p-4 mb-4 flex items-center">
              <h3 className="text-sm font-semibold text-light-text">画布</h3>
            </div>

            <VideoCanvas
              jobs={polling.jobs}
              onJobClick={(job) => {
                const playableUrl = job.playUrl || job.resultUrl;
                if (playableUrl) {
                  setLightboxJob({
                    url: playableUrl,
                    cover: job.coverProxyUrl || job.coverUrl,
                  });
                  setLightboxOpen(true);
                }
              }}
              onDownload={handleDownload}
              selectedRecentJob={selectedRecentJob}
              recentModelSupportsImg2Video={enabledModes.img2video}
              recentModelSupportsFirstLastFrame={enabledModes.first_last_frame}
              recentModeSupportsVideoReference={currentCaps.enableVideoReference}
              onCloseRecent={() => setSelectedRecentJob(null)}
              onDownloadRecent={handleDownload}
              onFillPromptFromRecent={(text) => {
                setPrompt(text);
                toast.success('已填充提示词');
              }}
              onFillReferenceFromRecent={(src) => {
                // 切换 mode（如果当前 mode 与历史任务不一致）
                if (src.mode !== mode) {
                  setMode(src.mode);
                }

                if (src.mode === 'img2video') {
                  // 追加：保留当前已有，再加上历史的，按当前模型上限去重 + 截断
                  const cap = currentCaps.maxReferenceImageCount || 1;
                  const merged = [...referenceImages, ...src.referenceImages];
                  const deduped = Array.from(new Set(merged)).slice(0, cap);
                  const added = deduped.length - referenceImages.length;
                  setReferenceImages(deduped);
                  toast.success(added > 0 ? `已追加 ${added} 张参考图` : '参考图均已存在');
                } else if (src.mode === 'first_last_frame') {
                  if (src.firstFrameImage) setFirstFrame(src.firstFrameImage);
                  if (src.lastFrameImage) setLastFrame(src.lastFrameImage);
                  toast.success('已填充首尾帧');
                }
              }}
              onFillVideoReferenceFromRecent={(urls) => {
                const cap = currentCaps.maxVideoReferenceCount || urls.length;
                const merged = [...videoReferenceUrls, ...urls];
                const deduped = Array.from(new Set(merged)).slice(0, cap);
                const added = deduped.length - videoReferenceUrls.length;
                // 保持时长数组与 URL 数组下标对齐；历史作品回填的条目时长未知，按 0 处理
                setVideoReferenceUrls(deduped);
                setVideoRefDurations(
                  deduped.map((url) => {
                    const idx = videoReferenceUrls.indexOf(url);
                    return idx >= 0 ? videoRefDurations[idx] ?? 0 : 0;
                  })
                );
                toast.success(added > 0 ? `已追加 ${added} 个参考视频` : '参考视频均已存在');
              }}
              onUseResultAsVideoReferenceFromRecent={(url) => {
                const cap = currentCaps.maxVideoReferenceCount || 1;
                const deduped = Array.from(new Set([...videoReferenceUrls, url])).slice(0, cap);
                const added = deduped.length - videoReferenceUrls.length;
                setVideoReferenceUrls(deduped);
                setVideoRefDurations(
                  deduped.map((item) => {
                    const idx = videoReferenceUrls.indexOf(item);
                    return idx >= 0 ? videoRefDurations[idx] ?? 0 : 0;
                  })
                );
                toast.success(added > 0 ? '已将当前视频加入参考视频' : '当前视频已在参考视频中');
              }}
            />

            <VideoLightbox
              videoUrl={lightboxJob?.url || null}
              coverUrl={lightboxJob?.cover}
              isOpen={lightboxOpen}
              onClose={() => {
                setLightboxOpen(false);
                setLightboxJob(null);
              }}
            />

            <VideoRecentWorks
              externalRefreshKey={recentRefreshKey}
              onPlay={(_job, detail) => {
                // 点击最近作品 → 装到画布顶部预览，不再开 Lightbox
                setSelectedRecentJob(detail);
              }}
            />
          </div>
        </div>
      </main>

      <Footer siteName={siteName} />
    </div>
  );
}

export default VideoPage;

function formatAllowedReferenceCounts(counts: number[]): string {
  const normalized = Array.from(new Set(counts))
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((a, b) => a - b);
  if (normalized.length === 0) return '特定参考图数量';
  if (normalized.length === 1) return `${normalized[0]} 张参考图`;
  return `${normalized.slice(0, -1).join(' 张或 ')} 张或 ${normalized[normalized.length - 1]} 张参考图`;
}

function buildReferenceImageCountMessage(modelName: string, allowedCounts: number[], currentCount: number): string {
  return `${modelName} 仅支持 ${formatAllowedReferenceCounts(allowedCounts)}，当前为 ${currentCount} 张`;
}
